import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { PdfDownloadMenuOptions } from 'features/knowledgeDocumentViewer/components/toolbar/PdfDownloadMenuOptions'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useDocumentDownloadActions } from 'features/knowledgeDocumentViewer/hooks/useDocumentDownloadActions'
import { usePdfOutlineUnits } from 'features/knowledgeDocumentViewer/hooks/usePdfOutlineUnits'
import {
  getDownloadDocumentKind,
  isOfficeDownloadKind,
} from 'features/knowledgeDocumentViewer/utils/downloadDocumentKind'
import { Download } from 'lucide-react'
import { useState } from 'react'

export function DownloadDocumentButton() {
  const { t } = useLingui()
  const {
    documentSignedURL,
    originalDocumentSignedURL,
    documentFilename,
    documentType,
    totalPages,
  } = useDocumentTabDataContext()
  const { currentPageNumber } = useKnowledgeDocumentUXContext()
  const [menuOpen, setMenuOpen] = useState(false)

  const pdfSignedUrl = documentSignedURL?.signed_url
  const originalSignedUrl = originalDocumentSignedURL?.signed_url
  const downloadKind = getDownloadDocumentKind(documentType, documentFilename)
  const isOffice = isOfficeDownloadKind(downloadKind)

  const showPdfPageMenu = downloadKind === 'pdf' && totalPages > 1
  const showOfficeMenu = isOffice
  const showDropdown = showPdfPageMenu || showOfficeMenu

  const { units: outlineUnits, isLoading: isOutlineLoading } =
    usePdfOutlineUnits(
      pdfSignedUrl,
      menuOpen &&
        (downloadKind === 'powerpoint' || downloadKind === 'spreadsheet')
    )

  const {
    isDownloading,
    handleFullPdfDownload,
    handleOriginalDownload,
    handleCurrentPageDownload,
    handleRangeDownload,
    handleOutlineUnitDownload,
  } = useDocumentDownloadActions({
    pdfSignedUrl,
    originalSignedUrl,
    documentFilename,
    currentPageNumber,
  })

  const isDisabled = isOffice
    ? (!pdfSignedUrl && !originalSignedUrl) || !documentFilename
    : !pdfSignedUrl || !documentFilename

  const pdfMenuProps = {
    totalPages,
    currentPageNumber,
    pdfSignedUrl,
    isDownloading,
    outlineUnits,
    isOutlineLoading,
    onCurrentPage: handleCurrentPageDownload,
    onFullPdf: handleFullPdfDownload,
    onRange: handleRangeDownload,
    onOutlineUnit: handleOutlineUnitDownload,
  }

  if (!showDropdown) {
    return (
      <Button
        variant='inputRaised'
        size='icon-lg'
        title={t`Download Document`}
        onClick={handleFullPdfDownload}
        disabled={isDisabled || isDownloading}>
        <Download size={18} strokeWidth={1.5} />
      </Button>
    )
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='inputRaised'
          size='icon-lg'
          title={t`Download Document`}
          disabled={isDisabled || isDownloading}>
          <Download size={18} strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {showPdfPageMenu && (
          <PdfDownloadMenuOptions variant='pdf-document' {...pdfMenuProps} />
        )}

        {showOfficeMenu && (
          <>
            <DropdownMenuItem
              onSelect={handleOriginalDownload}
              disabled={!originalSignedUrl}>
              {t`Download original`}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                {t`Download PDF version`}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {isOfficeDownloadKind(downloadKind) && (
                  <PdfDownloadMenuOptions
                    variant={downloadKind}
                    {...pdfMenuProps}
                  />
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
